/** `CanvasGlobalBrandBreath` — Canvas world's one-time brand atmosphere
 * (VISTOERA_CANVAS_SPLASH_AMBIENT_LANGUAGE_AND_VIEWPORT_RESTORE_V3).
 *
 * Ambient global language field — all elements exist from frame 0:
 *   - one per frontend runtime (module session flag)
 *   - pointer-events: none, viewport-relative (no pan/zoom tracking)
 *   - CSS-only animation with random negative phase delays
 *   - portaled into `.react-flow__renderer` at z-index 0 (below nodes). */

import { Component, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CANVAS_GLOBAL_BRAND_BREATH_WORDMARK } from './canvasGlobalBrandBreathData'
import {
  generateBrandBreathLayout,
  type BrandBreathItemLayout,
  type BrandBreathKeywordLayout,
} from './canvasGlobalBrandBreathLayout'
import { consumeCanvasGlobalBrandBreathEntitlement } from './canvasGlobalBrandBreathSession'
import { logCanvasBootstrapSummary, markCanvasBootstrap } from './canvasBootstrapTelemetry'
import {
  BRAND_BREATH_FINAL_FADE_START_MS,
  BRAND_BREATH_REDUCED_DURATION_MS,
  OPENING_SPLASH_DURATION_MS,
} from './canvasGlobalBrandBreathVisual'
import './canvas-global-brand-breath.css'

export const CANVAS_GLOBAL_BRAND_BREATH_ENABLED = true

function useReactFlowRendererPortal(): HTMLElement | null {
  const [target, setTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    const findRenderer = (): HTMLElement | null =>
      document.querySelector('.pcv-canvas-area .react-flow__renderer') as HTMLElement | null

    const existing = findRenderer()
    if (existing) {
      setTarget(existing)
      return
    }

    const area = document.querySelector('.pcv-canvas-area')
    if (!area) return

    const observer = new MutationObserver(() => {
      const renderer = findRenderer()
      if (renderer) {
        setTarget(renderer)
        observer.disconnect()
      }
    })
    observer.observe(area, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return target
}

function itemOuterStyle(item: BrandBreathItemLayout): CSSProperties {
  return {
    left: `${item.leftPercent}%`,
    top: `${item.topPercent}%`,
    fontSize: `${item.fontSizePx}px`,
    fontFamily: item.fontFamily,
    opacity: item.targetOpacity,
    ['--bb-opacity' as string]: item.targetOpacity,
    ['--bb-tx' as string]: `${item.translateXPx}px`,
    ['--bb-ty' as string]: `${item.translateYPx}px`,
  }
}

function keywordOuterStyle(kw: BrandBreathKeywordLayout): CSSProperties {
  return {
    left: `${kw.leftPercent}%`,
    top: `${kw.topPercent}%`,
    fontSize: `${kw.fontSizePx}px`,
    opacity: kw.targetOpacity,
    ['--bb-opacity' as string]: kw.targetOpacity,
    ['--bb-tx' as string]: '0px',
    ['--bb-ty' as string]: '0px',
  }
}

function innerMotionClass(direction: BrandBreathItemLayout['breathDirection']): string {
  switch (direction) {
    case 'recede':
      return 'pcv-brand-breath-inner--recede'
    case 'drift':
      return 'pcv-brand-breath-inner--drift'
    case 'fade-only':
      return 'pcv-brand-breath-inner--fade-only'
    case 'anchor-stable':
      return 'pcv-brand-breath-inner--anchor-stable'
    default:
      return ''
  }
}

function itemInnerStyle(item: BrandBreathItemLayout, reducedMotion: boolean): CSSProperties {
  if (reducedMotion) return {}
  return {
    ['--bb-breath-cycle' as string]: `${item.breathCycleMs}ms`,
    ['--bb-phase-delay' as string]: `${item.phaseDelayMs}ms`,
  }
}

function keywordInnerStyle(kw: BrandBreathKeywordLayout, reducedMotion: boolean): CSSProperties {
  if (reducedMotion) return {}
  return {
    ['--bb-breath-cycle' as string]: `${kw.breathCycleMs}ms`,
    ['--bb-phase-delay' as string]: `${kw.phaseDelayMs}ms`,
  }
}

function CanvasGlobalBrandBreathContent() {
  const [mounted, setMounted] = useState(true)
  const portalTarget = useReactFlowRendererPortal()
  const splashStartedAtRef = useRef<number | null>(null)
  if (splashStartedAtRef.current === null) {
    splashStartedAtRef.current = performance.now()
  }

  const reducedMotion = useMemo(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    } catch {
      return false
    }
  }, [])

  const layout = useMemo(() => generateBrandBreathLayout(reducedMotion), [reducedMotion])
  const totalDurationMs = reducedMotion ? BRAND_BREATH_REDUCED_DURATION_MS : OPENING_SPLASH_DURATION_MS
  const fadeStartMs = reducedMotion ? totalDurationMs * 0.7 : BRAND_BREATH_FINAL_FADE_START_MS

  useEffect(() => {
    if (!portalTarget) return
    markCanvasBootstrap('SPLASH_MOUNT_AT')
    const elapsed = performance.now() - (splashStartedAtRef.current ?? performance.now())
    const remaining = Math.max(0, totalDurationMs - elapsed)
    const fadeStartTimer = window.setTimeout(() => markCanvasBootstrap('SPLASH_FADE_START_AT'), Math.max(0, fadeStartMs - elapsed))
    const unmountTimer = window.setTimeout(() => {
      markCanvasBootstrap('SPLASH_UNMOUNT_AT')
      logCanvasBootstrapSummary()
      setMounted(false)
    }, remaining)
    return () => {
      window.clearTimeout(fadeStartTimer)
      window.clearTimeout(unmountTimer)
    }
  }, [portalTarget, totalDurationMs, fadeStartMs])

  if (!mounted || !portalTarget) return null

  const content = (
    <div
      className={`pcv-brand-breath-root${reducedMotion ? ' pcv-brand-breath-root--reduced' : ''}`}
      style={{
        pointerEvents: 'none',
        userSelect: 'none',
        ['--bb-total-duration' as string]: `${totalDurationMs}ms`,
      }}
      aria-hidden="true"
      data-brand-breath-layer="below-nodes"
      data-all-language-mounted="true"
    >
      {layout.keywords.map((kw) => (
        <span key={kw.id} className="pcv-brand-breath-keyword" style={keywordOuterStyle(kw)}>
          <span className="pcv-brand-breath-inner" style={keywordInnerStyle(kw, reducedMotion)}>
            {kw.text}
          </span>
        </span>
      ))}
      {layout.items.map((item) => (
        <span
          key={item.locale}
          className={[
            'pcv-brand-breath-item',
            `pcv-brand-breath-tier-${item.tier}`,
            `pcv-brand-breath-layer-${item.spatialLayer}`,
            item.isAnchor ? 'pcv-brand-breath-anchor' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={itemOuterStyle(item)}
          dir={item.direction}
        >
          <span
            className={['pcv-brand-breath-inner', !reducedMotion ? innerMotionClass(item.breathDirection) : '']
              .filter(Boolean)
              .join(' ')}
            style={itemInnerStyle(item, reducedMotion)}
          >
            {item.isAnchor ? (
              <span className="pcv-brand-breath-wordmark">{CANVAS_GLOBAL_BRAND_BREATH_WORDMARK}</span>
            ) : null}
            {item.text}
          </span>
        </span>
      ))}
    </div>
  )

  return createPortal(content, portalTarget)
}

interface GuardState {
  failed: boolean
}

class BrandBreathErrorGuard extends Component<{ children: ReactNode }, GuardState> {
  state: GuardState = { failed: false }

  static getDerivedStateFromError(): GuardState {
    return { failed: true }
  }

  componentDidCatch(error: unknown): void {
    console.error('[CanvasGlobalBrandBreath] suppressed error', error)
  }

  render(): ReactNode {
    if (this.state.failed) return null
    return this.props.children
  }
}

export default function CanvasGlobalBrandBreath() {
  const decidedRef = useRef<boolean | null>(null)
  if (decidedRef.current === null) {
    decidedRef.current = CANVAS_GLOBAL_BRAND_BREATH_ENABLED && consumeCanvasGlobalBrandBreathEntitlement()
  }

  if (!decidedRef.current) return null

  return (
    <BrandBreathErrorGuard>
      <CanvasGlobalBrandBreathContent />
    </BrandBreathErrorGuard>
  )
}
